import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { orderBy, has, set, get, split } from 'lodash/fp';
import { Table, Icon, IconButton } from 'rsuite';
import Pagination from '../../Pagination';
import DateCell from '../../widgets/custom-cell/Date';
import * as PERMISSIONS from '../../../constants/permissions/guest';
import { viewAccess } from '../../../helpers/permission';
import AccessControl from '../../AccessControl';
import PaginationType from '../../../interfaces/PaginationType';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import { FileType } from '../../../interfaces';
import AutoHeightTable from '../../AutoHeightTable';
import PathKeyCell from '../../widgets/custom-cell/PathKey';
import redirectTo from '../../../helpers/redirectTo';
import { createVisitUrl } from '../../../helpers/reservationUrls';
import history from '../../../routes/history';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import { guestsVisitTrackViewPath } from '../../../routes/Paths';

const { Column, HeaderCell, Cell } = Table;

const ActionCell = ({ rowData, dataKey, handleEdit, ...props }) => (
  <Cell {...props} className="link-group">
    <AccessControl allowOnlyFor={viewAccess(PERMISSIONS.MODULE)}>
      <IconButton
        appearance="link"
        style={{ top: '-8px' }}
        color="blue"
        icon={<Icon icon="eye" />}
        onClick={() => {
          history.push(
            pagePathToUrl(guestsVisitTrackViewPath, {
              id: get('client_id', rowData),
              visitId: get('id', rowData),
              visitTrackId: get('visitTrack.id', rowData)
            })
          );
        }}
      />
      <IconButton
        appearance="link"
        style={{ top: '-8px' }}
        color="blue"
        icon={<Icon icon="pencil" />}
        onClick={() => handleEdit(rowData)}
      />
    </AccessControl>
  </Cell>
);
ActionCell.propTypes = {
  rowData: FileType,
  dataKey: PropTypes.string.isRequired,
  handleEdit: PropTypes.func.isRequired
};
ActionCell.defaultProps = {
  rowData: null
};

class GuestVisitList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sortColumn: '',
      sortType: ''
    };
  }

  componentDidMount() {
    const { onInit, isFetched } = this.props;
    if (!isFetched) {
      onInit();
    }
  }

  getData() {
    const { data } = this.props;
    const { sortColumn = '', sortType = '' } = this.state;
    return orderBy(sortColumn, sortType, data);
  }

  handleSortColumn = (sortColumn, sortType) => {
    const { onSort, query } = this.props;
    onSort(sortColumn, sortType, query);
  };

  handleEditVisit = ({ id, outreach_id: outreachId, client_id: guestId }) => {
    redirectTo(
      createVisitUrl({
        id,
        outreachId,
        guestId
      }),
      true
    );
  };

  handleChangePage = page => {
    const { onChangePage, loadedPages, query } = this.props;
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  handleChangeLimit = limit => {
    const { onChangeLimit, query } = this.props;
    const newQuery = { ...query, limit, page: 1 };
    onChangeLimit(newQuery);
  };

  render() {
    const { isFetching, isFetched, query, pagination } = this.props;
    const [sortColumn, sortType] = split(':', get('order', query));

    return (
      <Fragment>
        <AutoHeightTable
          bordered
          reduceHeight={540}
          loading={isFetching || !isFetched}
          data={this.getData()}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={this.handleSortColumn}
        >
          <Column flexGrow={0.5} minWidth={50} align="center" sortable>
            <CustomHeaderCell>ID</CustomHeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column flexGrow={1} minWidth={110} sortable>
            <CustomHeaderCell>Visit Date</CustomHeaderCell>
            <DateCell dataKey="visit_on" format="ll" />
          </Column>
          <Column flexGrow={1.5} minWidth={130} sortable>
            <CustomHeaderCell>Tracking Method</CustomHeaderCell>
            <PathKeyCell dataKey="trackingMethod.label" />
          </Column>
          <Column flexGrow={1.5} minWidth={130} sortable>
            <CustomHeaderCell>Location</CustomHeaderCell>
            <PathKeyCell dataKey="location.name" />
          </Column>
          <Column flexGrow={1.5} minWidth={130} sortable>
            <CustomHeaderCell>Team Member</CustomHeaderCell>
            <PathKeyCell dataKey="volunteer.displayName" />
          </Column>
          <Column flexGrow={0.8} minWidth={100}>
            <HeaderCell align="center">Action</HeaderCell>
            <ActionCell
              align="center"
              dataKey="id"
              handleEdit={this.handleEditVisit}
            />
          </Column>
        </AutoHeightTable>

        <Pagination
          page={pagination.currentPage}
          limit={pagination.limit}
          total={pagination.totalCount}
          onChangePage={this.handleChangePage}
          onChangeLimit={this.handleChangeLimit}
        />
      </Fragment>
    );
  }
}

GuestVisitList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFetching: PropTypes.bool.isRequired,
  isFetched: PropTypes.bool.isRequired,
  onInit: PropTypes.func.isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired
};

export default GuestVisitList;
